// The single axios instance for the app (never the global default). Token
// injection and envelope unwrapping happen in axiosBaseQuery so this stays
// free of any store import (avoids a store ⇄ api circular dependency).

import axios from 'axios';
import { ApiError, envelopeErrorMsg } from './types';
import { decryptApiData, encryptApiData } from '@/config/security';
import { DEV_ONLY, SKIP_ENCRYPT } from '@/config/config';

export const client = axios.create({
  baseURL: import.meta.env.VITE_API_PATH,
  headers: { 'content-type': 'application/json', skipencrypt: SKIP_ENCRYPT },
});
const publicApisList = [
  'users/login',
  'users/register',
  'users/send-otp',
  'users/verify-otp',
  'users/reset-password',
];
const storeKey = 'persist:midani_' + import.meta.env.VITE_ENVIRONMENT;

client.interceptors.request.use(async (config) => {
  if (config.url !== 'startup') {
    const securityData = JSON.parse(JSON.parse(localStorage.getItem(storeKey)).security);
    const isPublicApis = publicApisList.includes(config.url);
    const requestData = isPublicApis
      ? { ...config.data, publicKey: securityData.localPublicKey }
      : config.data;
    if (!config.headers.skipencrypt) {
      if (requestData) {
        if (DEV_ONLY) console.log(config.url, requestData);
        const encryptedData = await encryptApiData(securityData.serverPublicKey, requestData);
        config.data = encryptedData;
      }
    } else config.data = requestData;
  }
  return config;
});

// Normalize transport/HTTP errors into an ApiError; leave the body untouched
// (the base query reads the { success, data, error } envelope).
client.interceptors.response.use(
  async (response) => {
    try {
      if (response.config.url !== 'startup') {
        const securityData = JSON.parse(JSON.parse(localStorage.getItem(storeKey)).security);
        const isPublicApis = publicApisList.includes(response.config.url);
        if (!response.config.headers.skipencrypt) {
          const decryptedData = await decryptApiData(
            isPublicApis ? securityData.localPrivateKey : securityData.serverPrivateKey,
            response.data,
          );
          response.data = decryptedData;
          if (DEV_ONLY) console.log(response.config.url, response.data);
        }
      }
      return response;
    } catch (error) {
      console.error('Error decrypting response data:', error);
      throw new ApiError('Error decrypting response data', 500);
    }
  },
  (error) => {
    // The body's `error` is a string or a string array (validation errors) —
    // both passed through verbatim — or an object carrying `msg`. This is the
    // only path that sees the array shape (a 2xx `success:false` never carries
    // one). The envelope's `message` is success-only (present but "" on a
    // failure), so the fallback is axios' own text, never that key.
    const exceptionMessage = envelopeErrorMsg(error.response?.data?.error) ?? error.message;

    return Promise.reject(new ApiError(exceptionMessage, error.response?.status));
  },
);
